import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { newServer } from 'constants/constants';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

interface CreateSubscriptionDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedSub?: any;
  taxOptions?: boolean[];
  currencyOptions?: string[];
  billingCycles?: string[];
  trialPeriods?: number[];
  statusOptions?: string[];
}

export default function CreateSubscriptionDrawer({
  open,
  onClose,
  selectedSub,
  taxOptions = [true, false],
  currencyOptions = ['USD', 'EUR', 'MXN'],
  billingCycles = [
    'Monthly End of Month',
    'Monthly End of Month Fixed',
    'Monthly Start of Month Fixed',
    'Yearly Fixed',
  ],
  trialPeriods = [7, 14, 30],
  statusOptions = ['Active', 'Inactive', 'Pending'],
}: CreateSubscriptionDrawerProps) {
  // Estados del formulario
  const [marketingTitle, setMarketingTitle] = useState('');
  const [tax, setTax] = useState(true);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [selectedBillingCycles, setSelectedBillingCycles] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [selectedTrialPeriod, setSelectedTrialPeriod] = useState(7);
  const [selectedStatus, setSelectedStatus] = useState(true);
  const [amountFixed, setAmountFixed] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [howBillWeb, setHowBillWeb] = useState(true);
  const [howBillEmployee, setHowBillEmployee] = useState(true);
  const [webQuantity, setWebQuantity] = useState(0);
  const [employeeQuantity, setEmployeeQuantity] = useState(0);
  const [webDailyRate, setWebDailyRate] = useState(0);
  const [employeeDailyRate, setEmployeeDailyRate] = useState(0);
  const [newFeature, setNewFeature] = useState('');
  const [features, setFeatures] = useState<string[]>([]);
  const previewPlan = {
    id: 1,
    name: marketingTitle || 'Untitled Plan',
    price: amountFixed !== '' ? amountFixed : '0',
    description: description || 'No description provided',
    features: features,
  };
  const token = localStorage.getItem('token');

  const clearForm = () => {
    setMarketingTitle('');
    setTax(true);
    setSelectedCurrencies([]);
    setSelectedBillingCycles([]);
    setDescription('');
    setSelectedTrialPeriod(7);
    setSelectedStatus(true);
    setAmountFixed('');
    setTaxAmount('');
    setHowBillWeb(true);
    setHowBillEmployee(true);
    setWebQuantity(0);
    setEmployeeQuantity(0);
    setWebDailyRate(0);
    setEmployeeDailyRate(0);
    setNewFeature('');
    setFeatures([]);
  };

  const validateForm = (): boolean => {
    if (!marketingTitle.trim()) {
      alert('Marketing title is required.');
      return false;
    }
    if (selectedCurrencies.length === 0) {
      alert('At least one currency must be selected.');
      return false;
    }
    if (selectedBillingCycles.length === 0) {
      alert('At least one billing cycle must be selected.');
      return false;
    }
    if (!amountFixed.trim()) {
      alert('Amount Fixed is required.');
      return false;
    }
    return true;
  };

  const handleCreateOrUpdateSubscription = async () => {
    if (!validateForm()) return;

    const chargeusers = {
      web: webQuantity,
      employee: employeeQuantity,
      web_rate: webDailyRate,
      employee_rate: employeeDailyRate,
      howBillEmployee,
      howBillWeb,
    };

    const subscriptionData = {
      marketingTitle,
      tax,
      currencies: selectedCurrencies[0],
      billingCycles: selectedBillingCycles[0],
      description,
      trialPeriod: selectedTrialPeriod,
      status: selectedStatus,
      amountFixed,
      taxAmount,
      chargeusers,
      features: JSON.stringify(features ? features : []),
    };

    try {
      const url = selectedSub
        ? `${newServer}subscriptions/${selectedSub.subscribe_rate_id}`
        : `${newServer}subscriptions/`;
      const method = selectedSub ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(subscriptionData),
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription data.');
      }

      clearForm();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      alert('Something went wrong. Please try again.');
    }
  };

  useEffect(() => {
    if (selectedSub) {
      setMarketingTitle(selectedSub.marketing_title || '');
      setTax(selectedSub.automatic_tax ?? true);
      setSelectedCurrencies(selectedSub.currency ? [selectedSub.currency] : []);
      setSelectedBillingCycles(selectedSub.billingmethod ? [selectedSub.billingmethod] : []);
      setDescription(selectedSub.marketing_description || '');
      setSelectedTrialPeriod(selectedSub.days_of_trail_period || 7);
      setSelectedStatus(selectedSub.hidden || true);
      setAmountFixed(selectedSub.fixed_value || '');
      setTaxAmount(selectedSub.tax_amount || '');
      setFeatures(selectedSub.feature || []);
      setHowBillWeb(selectedSub.chargeusers?.howBillWeb || true);
      setHowBillEmployee(selectedSub.chargeusers?.howBillEmployee || true);
      setWebQuantity(selectedSub.chargeusers?.web || 0);
      setEmployeeQuantity(selectedSub.chargeusers?.employee || 0);
      setWebDailyRate(selectedSub.chargeusers?.web_rate || 0);
      setEmployeeDailyRate(selectedSub.chargeusers?.employee_rate || 0);
    } else {
      clearForm();
    }
  }, [selectedSub]);

  // Drawer de previsualización a la izquierda
  const leftDrawer = (
    <Drawer
      anchor='left'
      open={open} // Mismo control que el Drawer derecho, puedes manejarlo de forma independiente
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 }, padding: 2, backgroundColor: '#f9fafb' },
      }}
    >
      <Typography variant='h5' fontWeight='bold' gutterBottom>
        Subscription Preview
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid item xs={12} md={12}>
        <Card
          sx={{
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'default',
            transition: 'all 0.3s ease',
            borderRadius: 4,
            border: 1,
            borderColor: 'grey.300',
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant='h5' component='div'>
                {previewPlan.name}
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant='h4' component='div' sx={{ fontWeight: 'bold' }}>
                  ${previewPlan.price}
                </Typography>
                <Typography variant='subtitle1' color='text.secondary'>
                  /month
                </Typography>
              </Box>
            </Box>

            <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
              {previewPlan.description}
            </Typography>

            <List>
              {previewPlan.features.map((feature, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircleOutlineIcon sx={{ color: '#111827' }} />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </CardContent>

          <CardActions sx={{ p: 2 }}>
            <Button
              fullWidth
              variant='contained'
              sx={{
                bgcolor: '#111827',
                borderColor: '#111827',
                '&:hover': {
                  bgcolor: '#1f2937',
                },
              }}
            >
              Selected Plan
            </Button>
          </CardActions>
        </Card>
      </Grid>
    </Drawer>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {leftDrawer}
      <Drawer
        anchor='right'
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 600 }, p: 3, borderRadius: '8px' },
        }}
      >
        {/* Icono para cerrar el Drawer */}
        <Box display='flex' justifyContent='flex-end' mb={2}>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant='h5' fontWeight='bold' mb={2}>
          {selectedSub ? 'Edit Subscription Structure' : 'Create Subscription Structure'}
        </Typography>

        <Grid container spacing={3}>
          {/* Marketing Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Marketing Title'
              variant='outlined'
              value={marketingTitle}
              onChange={(e) => setMarketingTitle(e.target.value)}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          {/* Tax */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Tax</InputLabel>
              <Select
                label='Tax'
                value={tax ? 'Active' : 'Inactive'}
                onChange={(e) => setTax(e.target.value === 'Active')}
              >
                <MenuItem value='Active'>Active</MenuItem>
                <MenuItem value='Inactive'>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Currency */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                label='Currency'
                value={selectedCurrencies[0] || ''}
                onChange={(e) => setSelectedCurrencies([e.target.value])}
              >
                {currencyOptions.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Billing Cycle */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Billing Cycle</InputLabel>
              <Select
                label='Billing Cycle'
                value={selectedBillingCycles[0] || ''}
                onChange={(e) => setSelectedBillingCycles([e.target.value])}
              >
                {billingCycles.map((cycle) => (
                  <MenuItem key={cycle} value={cycle}>
                    {cycle}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              multiline
              rows={4}
              fullWidth
              label='Description'
              variant='outlined'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          {/* Trial Period */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Trial Period</InputLabel>
              <Select
                label='Trial Period'
                value={selectedTrialPeriod || ''}
                onChange={(e) => setSelectedTrialPeriod(Number(e.target.value))}
              >
                {trialPeriods.map((period) => (
                  <MenuItem key={period} value={period}>
                    {period} days
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label='Status'
                value={selectedStatus ? 'Active' : 'Inactive'}
                onChange={(e) => setSelectedStatus(e.target.value === 'Active')}
              >
                <MenuItem value='Active'>Active</MenuItem>
                <MenuItem value='Inactive'>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Amount Fixed */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Amount Fixed'
              type='number'
              variant='outlined'
              value={amountFixed || ''}
              onChange={(e) => setAmountFixed(e.target.value)}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          {/* Tax Amount */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Tax Amount'
              type='number'
              variant='outlined'
              value={taxAmount || ''}
              onChange={(e) => setTaxAmount(e.target.value)}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Features Section */}
        <Typography variant='h6' fontWeight='bold' mb={2}>
          Features
        </Typography>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Box display='flex' alignItems='center' gap={2}>
            <TextField
              fullWidth
              label='Add Feature'
              variant='outlined'
              size='small'
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              sx={{ backgroundColor: '#f9f9f9', borderRadius: 1 }}
            />
            <Button
              variant='contained'
              sx={{ textTransform: 'none', boxShadow: 'none' }}
              onClick={() => {
                if (newFeature.trim()) {
                  setFeatures([...features, newFeature.trim()]);
                  setNewFeature('');
                }
              }}
            >
              Add
            </Button>
          </Box>
          {features.length > 0 && (
            <Box mt={3}>
              {features.map((feature, index) => (
                <Paper
                  key={index}
                  elevation={1}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant='body1'>{feature}</Typography>
                  <IconButton
                    color='error'
                    size='small'
                    onClick={() => setFeatures(features.filter((_, i) => i !== index))}
                  >
                    <CloseIcon />
                  </IconButton>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant='outlined' color='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button variant='contained' color='primary' onClick={handleCreateOrUpdateSubscription}>
            {selectedSub ? 'Edit Subscription' : 'Create Subscription'}
          </Button>
        </Box>
      </Drawer>
    </LocalizationProvider>
  );
}
